/**
 * TemporaryBarrier.js - Large Temporary Barriers for Memex Racing Game
 * 
 * VERY BIG barriers (200x400 pixels) that appear/disappear during races
 * Creates dramatic track changes and strategic chokepoints
 */

import Phaser from 'phaser';

/**
 * TemporaryBarrier class for large dynamic walls
 */
export class TemporaryBarrier extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, barrierType, config = {}) {
    const texture = barrierType.texture || 'barrier_default';
    super(scene, x, y, texture);
    
    // Add to scene and enable physics
    scene.add.existing(this);
    scene.physics.add.existing(this);
    
    // Store barrier properties
    this.barrierType = barrierType;
    this.scene = scene;
    this.active = true;
    
    // Physics setup - VERY BIG as requested
    this.body.setImmovable(true);
    this.setDisplaySize(barrierType.width, barrierType.height);
    this.body.setSize(barrierType.width, barrierType.height);
    
    // Visual setup - HIGH depth to be very visible
    this.setDepth(400); // Even higher than obstacles
    this.setTint(barrierType.tint || 0x00ffff);
    
    // Barrier lifecycle
    this.state = 'warning'; // warning -> appearing -> active -> disappearing -> destroyed
    this.warningDuration = config.warningDuration || 3000; // 3 seconds warning
    this.activeDuration = config.activeDuration || (10000 + Math.random() * 5000); // 10-15 seconds
    this.spawnTime = scene.time.now;
    
    // Movement properties for dynamic barriers
    this.movementType = barrierType.movement || 'static';
    this.moveSpeed = barrierType.moveSpeed || 0;
    this.moveDirection = barrierType.moveDirection || 0;
    this.moveAmplitude = barrierType.moveAmplitude || 0;
    this.originalX = x;
    this.originalY = y;
    this.moveTimer = 0;
    
    // Visual effects
    this.warningZone = null;
    this.glowEffects = [];
    this.warningText = null;
    
    // Start with warning phase
    this.startWarningPhase();
    
    console.log(`[TemporaryBarrier] Created LARGE ${barrierType.name} at (${x}, ${y}) - ${barrierType.width}x${barrierType.height}`);
  }
  
  /**
   * Start warning phase with red zone preview
   */
  startWarningPhase() {
    console.log(`[TemporaryBarrier] Starting warning phase for ${this.barrierType.name}`);
    
    // Make barrier invisible during warning
    this.setAlpha(0);
    
    // Create large red warning zone
    this.warningZone = this.scene.add.rectangle(
      this.x, this.y,
      this.barrierType.width + 20,
      this.barrierType.height + 20,
      0xff0000, 0.4
    );
    this.warningZone.setDepth(this.depth - 1);
    
    // Flashing warning effect
    this.scene.tweens.add({
      targets: this.warningZone,
      alpha: { from: 0.4, to: 0.8 },
      duration: 400,
      yoyo: true,
      repeat: Math.floor(this.warningDuration / 800)
    });
    
    // Add warning text
    this.warningText = this.scene.add.text(
      this.x, this.y - this.barrierType.height / 2 - 30,
      'BARRIER INCOMING!',
      {
        fontSize: '24px',
        fontFamily: 'Courier New',
        color: '#ff0000',
        fontWeight: 'bold',
        stroke: '#ffffff',
        strokeThickness: 2
      }
    ).setOrigin(0.5).setDepth(this.depth + 1);
    
    // Pulsing warning text
    this.scene.tweens.add({
      targets: this.warningText,
      scale: { from: 1, to: 1.3 },
      duration: 300,
      yoyo: true,
      repeat: -1
    });
    
    // Create countdown effect
    let countdown = Math.floor(this.warningDuration / 1000);
    const countdownText = this.scene.add.text(
      this.x, this.y,
      countdown.toString(),
      {
        fontSize: '48px',
        fontFamily: 'Courier New',
        color: '#ffff00',
        fontWeight: 'bold',
        stroke: '#000000',
        strokeThickness: 4
      }
    ).setOrigin(0.5).setDepth(this.depth + 2);
    
    const countdownTimer = this.scene.time.addEvent({
      delay: 1000,
      repeat: countdown - 1,
      callback: () => {
        countdown--;
        if (countdown > 0) {
          countdownText.setText(countdown.toString());
        } else {
          countdownText.destroy();
        }
      }
    });
    
    // Schedule appearance
    this.scene.time.delayedCall(this.warningDuration, () => {
      this.startAppearingPhase();
    });
  }
  
  /**
   * Barrier appears with dramatic effect
   */
  startAppearingPhase() {
    console.log(`[TemporaryBarrier] ${this.barrierType.name} APPEARING!`);
    
    this.state = 'appearing';
    
    // Clean up warning effects
    if (this.warningZone) {
      this.warningZone.destroy();
      this.warningZone = null;
    }
    if (this.warningText) {
      this.warningText.destroy();
      this.warningText = null;
    }
    
    // Screen shake for dramatic effect
    this.scene.cameras.main.shake(300, 0.02);
    
    // Barrier appears with scale animation
    this.setAlpha(1);
    this.setScale(0.1);
    
    this.scene.tweens.add({
      targets: this,
      scaleX: 1,
      scaleY: 1,
      duration: 500,
      ease: 'Back.out',
      onComplete: () => {
        this.startActivePhase();
      }
    });
    
    // Create dramatic appearance particles
    for (let i = 0; i < 12; i++) {
      const angle = (Math.PI * 2 * i) / 12;
      const distance = Math.max(this.barrierType.width, this.barrierType.height) / 2;
      
      const particle = this.safeSceneAdd('circle',
        this.x + Math.cos(angle) * distance,
        this.y + Math.sin(angle) * distance,
        8, this.barrierType.tint || 0x00ffff, 1
      );
      
      if (particle) {
        particle.setDepth(this.depth + 1);
        
        this.safeTween({
          targets: particle,
          x: this.x + Math.cos(angle) * distance * 2,
          y: this.y + Math.sin(angle) * distance * 2,
          alpha: 0,
          scale: 0,
          duration: 800,
          onComplete: () => particle.destroy()
        });
      }
    }
  }
  
  /**
   * Barrier is now active and solid
   */
  startActivePhase() {
    console.log(`[TemporaryBarrier] ${this.barrierType.name} is now ACTIVE!`);
    
    this.state = 'active';
    
    // Create glowing border effect
    this.createGlowEffects();
    
    // Schedule disappearance warning
    const disappearWarningTime = this.activeDuration - 2000; // 2 seconds warning
    this.scene.time.delayedCall(disappearWarningTime, () => {
      this.startDisappearingWarning();
    });
    
    // Schedule disappearance
    this.scene.time.delayedCall(this.activeDuration, () => {
      this.startDisappearingPhase();
    });
  }
  
  /**
   * Safely create a tween if scene exists
   * @param {Object} config - Tween configuration
   * @returns {Phaser.Tweens.Tween|null}
   */
  safeTween(config) {
    if (!this.scene || !this.scene.tweens) {
      console.warn(`[TemporaryBarrier] Scene is undefined, cannot create tween`);
      return null;
    }
    return this.scene.tweens.add(config);
  }

  /**
   * Safely create scene objects if scene exists
   * @param {string} type - Type of object to create (rectangle, graphics, circle, text)
   * @param {...any} args - Arguments for the creation method
   * @returns {Phaser.GameObjects.*|null}
   */
  safeSceneAdd(type, ...args) {
    if (!this.scene || !this.scene.add || !this.scene.add[type]) {
      console.warn(`[TemporaryBarrier] Scene is undefined, cannot create ${type}`);
      return null;
    }
    return this.scene.add[type](...args);
  }

  /**
   * Warn that barrier is about to disappear
   */
  startDisappearingWarning() {
    console.log(`[TemporaryBarrier] ${this.barrierType.name} disappearing soon!`);
    
    // Flashing effect to warn of disappearance using safe tween
    this.safeTween({
      targets: this,
      alpha: { from: 1, to: 0.3 },
      duration: 200,
      yoyo: true,
      repeat: 9 // Flash for 2 seconds
    });
  }
  
  /**
   * Barrier disappears
   */
  startDisappearingPhase() {
    console.log(`[TemporaryBarrier] ${this.barrierType.name} DISAPPEARING!`);
    
    this.state = 'disappearing';
    
    // Disable physics so players can pass through (if body still exists)
    if (this.body) {
      this.body.setSize(1, 1);
    }
    
    // Shrink and fade out using safe tween
    this.safeTween({
      targets: this,
      scaleX: 0,
      scaleY: 0,
      alpha: 0,
      duration: 500,
      ease: 'Back.in',
      onComplete: () => {
        this.destroy();
      }
    });
    
    // Create disappear particles using safe scene access
    for (let i = 0; i < 8; i++) {
      const particle = this.safeSceneAdd('rectangle',
        this.x + (Math.random() - 0.5) * this.barrierType.width,
        this.y + (Math.random() - 0.5) * this.barrierType.height,
        10, 10,
        this.barrierType.tint || 0x00ffff
      );
      
      if (particle) {
        particle.setDepth(this.depth);
        
        this.safeTween({
          targets: particle,
          y: particle.y - 50,
          alpha: 0,
          scale: 0,
          duration: 1000,
          onComplete: () => particle.destroy()
        });
      }
    }
  }
  
  /**
   * Create glowing effects around barrier
   */
  createGlowEffects() {
    // Create multiple glow rings for dramatic effect
    for (let i = 0; i < 3; i++) {
      const glowRing = this.safeSceneAdd('graphics');
      
      if (glowRing) {
        glowRing.lineStyle(6 - i * 2, this.barrierType.tint || 0x00ffff, 0.6 - i * 0.2);
        glowRing.strokeRect(
          this.x - this.barrierType.width / 2 - (i * 10),
          this.y - this.barrierType.height / 2 - (i * 10),
          this.barrierType.width + (i * 20),
          this.barrierType.height + (i * 20)
        );
        glowRing.setDepth(this.depth - 1 - i);
        
        // Pulsing glow effect using safe tween
        this.safeTween({
          targets: glowRing,
          alpha: { from: 0.6 - i * 0.2, to: 0.9 - i * 0.2 },
          duration: 1000 + i * 200,
          yoyo: true,
          repeat: -1
        });
      }
      
      this.glowEffects.push(glowRing);
    }
  }
  
  /**
   * Update barrier behavior
   */
  update(time, delta) {
    if (!this.active || this.state !== 'active') return;
    
    // Update movement for dynamic barriers
    if (this.movementType !== 'static') {
      this.updateMovement(time, delta);
    }
    
    this.moveTimer += delta;
  }
  
  /**
   * Update movement patterns for dynamic barriers
   */
  updateMovement(time, delta) {
    switch (this.movementType) {
      case 'oscillate':
        // Move back and forth across the track
        const oscillation = Math.sin(this.moveTimer * 0.001 * this.moveSpeed) * this.moveAmplitude;
        if (this.moveDirection === 'horizontal') {
          this.x = this.originalX + oscillation;
          // Update glow effects position
          this.glowEffects.forEach(glow => {
            glow.x = this.x;
          });
        } else {
          this.y = this.originalY + oscillation;
          // Update glow effects position
          this.glowEffects.forEach(glow => {
            glow.y = this.y;
          });
        }
        break;
        
      case 'rotate':
        // Rotate the barrier itself
        this.rotation += this.moveSpeed * delta * 0.001;
        break;
    }
  }
  
  /**
   * Handle collision with player
   */
  handlePlayerCollision(player) {
    if (this.state !== 'active') return;
    
    // Strong knockback effect
    const angle = Math.atan2(player.y - this.y, player.x - this.x);
    const knockbackForce = 150;
    
    player.x += Math.cos(angle) * knockbackForce;
    player.y += Math.sin(angle) * knockbackForce;
    
    // Visual impact effect
    player.setTint(0xff0000);
    this.scene.time.delayedCall(300, () => player.clearTint());
    
    // Screen shake on impact
    this.scene.cameras.main.shake(200, 0.01);
    
    // Spark particles at impact point
    for (let i = 0; i < 6; i++) {
      const spark = this.safeSceneAdd('circle',
        player.x + (Math.random() - 0.5) * 30,
        player.y + (Math.random() - 0.5) * 30,
        3, 0xffff00, 1
      );
      
      if (spark) {
        spark.setDepth(this.depth + 2);
        
        this.safeTween({
          targets: spark,
          y: spark.y - 20,
          alpha: 0,
          scale: 0,
          duration: 400,
          onComplete: () => spark.destroy()
        });
      }
    }
    
    console.log(`[TemporaryBarrier] Player ${player.index} hit LARGE barrier!`);
  }
  
  /**
   * Clean up and destroy barrier
   */
  destroy() {
    // Clean up all visual effects
    if (this.warningZone) {
      this.warningZone.destroy();
    }
    if (this.warningText) {
      this.warningText.destroy();
    }
    
    this.glowEffects.forEach(glow => {
      if (glow) glow.destroy();
    });
    this.glowEffects = [];
    
    console.log(`[TemporaryBarrier] ${this.barrierType.name} destroyed`);
    
    super.destroy();
  }
}

/**
 * Large barrier types - ALL ARE VERY BIG (200x400 pixels)
 */
export const BARRIER_TYPES = {
  SOLID_WALL: {
    name: 'solid_wall',
    texture: 'barrier_solid',
    width: 200,
    height: 400,
    tint: 0x00ffff,
    movement: 'static',
    description: 'Massive solid wall blocking the path'
  },
  
  SLIDING_WALL: {
    name: 'sliding_wall',
    texture: 'barrier_sliding',
    width: 200,
    height: 400,
    tint: 0xff00ff,
    movement: 'oscillate',
    moveDirection: 'horizontal',
    moveSpeed: 1,
    moveAmplitude: 300,
    description: 'Large wall that slides across the track'
  },
  
  RISING_WALL: {
    name: 'rising_wall',
    texture: 'barrier_rising',
    width: 400,
    height: 200,
    tint: 0xffff00,
    movement: 'static',
    description: 'Wide barrier that rises from the ground'
  },
  
  ROTATING_WALL: {
    name: 'rotating_wall',
    texture: 'barrier_rotating',
    width: 300,
    height: 300,
    tint: 0xff6600,
    movement: 'rotate',
    moveSpeed: 2,
    description: 'Large rotating square barrier'
  },
  
  GATE_WALL: {
    name: 'gate_wall',
    texture: 'barrier_gate',
    width: 200,
    height: 350,
    tint: 0x00ff00,
    movement: 'static',
    description: 'Tall gate-like barrier with openings'
  }
};

export default TemporaryBarrier;